<template>
  <section class="k12-panel k12-panel-stretch">
    <div class="k12-panel-header">
      <div class="k12-panel-title">
        <span class="k12-icon"><i class="fas fa-user-graduate"></i></span>
        <span>{{ $t('preInvoiceV2.studentSummary') }}</span>
      </div>
    </div>
    <div class="k12-panel-body ts-space-y-3">
      <a-radio-group v-model="model.student_type" button-style="solid" @change="$emit('student-type-change')">
        <a-radio-button value="old">{{ $t('preInvoiceV2.oldStudent') }}</a-radio-button>
        <a-radio-button v-if="!isEdit" value="fresh">{{ $t('preInvoiceV2.freshStudent') }}</a-radio-button>
      </a-radio-group>

      <div v-if="model.student_type === 'old'" class="ts-space-y-3">
        <div v-if="!selectedEnrollment" class="student-select-field">
          <div class="student-select-label-row">
            <label class="required compact-label">{{ $t('preInvoiceV2.selectStudent') }}</label>
            <span>{{ $t('preInvoiceV2.currentNextOnly') }}</span>
          </div>
          <div class="student-select-search-hint">{{ $t('preInvoiceV2.oldStudentSearchHint') }}</div>
          <a-select v-model="model.enrollment_id" class="ts-w-full student-select" show-search :filter-option="false"
            :default-active-first-option="false" :loading="loadingEnrollments" :dropdown-match-select-width="false"
            :dropdown-style="{ width: '980px', maxWidth: 'calc(100vw - 48px)' }"
            :placeholder="$t('preInvoiceV2.searchStudent')" @search="studentKeyword = $event"
            @inputKeydown="onStudentKeydown" @change="$emit('select-enrollment', $event)">
            <a-icon slot="suffixIcon" type="search" class="student-select-search-icon"
              @click.stop="searchEnrollments(studentKeyword)" />
            <a-select-option v-for="student in enrollments" :key="student.enrollment_id" :value="student.enrollment_id">
              <div class="student-option">
                <span class="student-option-heading">
                  <span class="student-option-avatar">
                    <img v-if="enrollmentPhoto(student)" :src="enrollmentPhoto(student)" alt="" />
                    <span v-else>{{ getEnrollmentInitials(student) }}</span>
                  </span>
                  <span class="student-option-title">
                    <span class="student-option-name">{{ getEnrollmentName(student) }}</span>
                    <span class="student-option-code">{{ student.card_number }}</span>
                  </span>
                </span>
                <span class="student-option-grid">
                  <span class="student-option-field">
                    <small>{{ $t('preInvoiceV2.mainProgram') }}</small>
                    <strong>{{ student.acp_name_foreign || '-' }}</strong>
                  </span>
                  <span class="student-option-field">
                    <small>{{ $t('preInvoiceV2.subProgram') }}</small>
                    <strong>{{ student.acps_name_foreign || '-' }}</strong>
                  </span>
                  <span class="student-option-field">
                    <small>{{ $t('preInvoiceV2.academicTerm') }}</small>
                    <strong>{{ student.term_name || '-' }}</strong>
                  </span>

                  <span class="student-option-field">
                    <small>{{ $t('preInvoiceV2.class') }}</small>
                    <strong class="student-option-class">
                      <span>{{ student.class_name || '-' }}</span>
                      <span class="student-option-status">{{ academicStatusName(student) }}</span>
                    </strong>
                  </span>
                  <span class="student-option-field">
                    <small>{{ $t('preInvoiceV2.teacher') }}</small>
                    <strong>{{ teacherName(student) }}</strong>
                  </span>
                  <span class="student-option-field">
                    <small>{{ $t('preInvoiceV2.studyTime') }}</small>
                    <strong>{{ student.study_time || '-' }}</strong>
                  </span>

                  <span class="student-option-field">
                    <small>{{ $t('preInvoiceV2.grade') }}</small>
                    <strong>{{ student.grade_name_foreign || '-' }}</strong>
                  </span>
                </span>
              </div>
            </a-select-option>
          </a-select>
          <div class="student-select-helper">{{ $t('preInvoiceV2.studentDropdownFieldsHint') }}</div>
        </div>

        <div v-if="selectedEnrollment" class="student-card">
          <div class="student-avatar">
            <img v-if="selectedStudentPhoto" :src="selectedStudentPhoto" alt="" />
            <span v-else>{{ selectedStudentInitials }}</span>
          </div>
          <div class="student-card-main">
            <div class="ts-flex ts-justify-between ts-gap-2">
              <div class="ts-min-w-0">
                <div class="student-card-name">{{ selectedStudentName }}</div>
                <div class="student-card-meta"> {{ selectedEnrollment.last_name_kh
                  }} {{ selectedEnrollment.first_name_kh }}</div>
              </div>
              <a-button size="small" icon="close" @click="$emit('clear-selected')">
                {{ $t('preInvoiceV2.clear') }}
              </a-button>
            </div>
            <div class="student-card-grid">
              <span><i class="fas fa-id-badge"></i>{{ selectedEnrollment.card_number }}</span>
              <span><i class="fas fa-venus-mars"></i>{{ selectedEnrollment.gender }}</span>
              <span><i class="fas fa-book-open"></i>{{ selectedEnrollment.acp_name_foreign }}</span>
              <span><i class="fas fa-layer-group"></i>{{ selectedEnrollment.acps_name_foreign }}</span>
              <span><i class="fas fa-calendar-alt"></i>{{ selectedEnrollment.term_name }}</span>
              <span><i class="fas fa-graduation-cap"></i>{{ selectedEnrollment.grade_name_foreign }}</span>
              <span><i class="fas fa-chalkboard"></i>{{ selectedEnrollment.class_name }}</span>
              <span><i class="fas fa-user-tie"></i>{{ teacherName(selectedEnrollment) }}</span>
              <span><i class="fas fa-circle"></i>{{ academicStatusName(selectedEnrollment) }}</span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="model.student_type === 'fresh'" class="new-student-grid">
        <div>
          <label class="required compact-label">{{ $t('preInvoiceV2.lastNameEn') }}</label>
          <a-input v-model="model.student.last_name_en" :placeholder="$t('preInvoiceV2.lastNameEn')" />
        </div>
        <div>
          <label class="required compact-label">{{ $t('preInvoiceV2.firstNameEn') }}</label>
          <a-input v-model="model.student.first_name_en" :placeholder="$t('preInvoiceV2.firstNameEn')" />
        </div>
        <div>
          <label class="compact-label">{{ $t('preInvoiceV2.dateOfBirth') }}</label>
          <a-date-picker v-model="model.student.date_of_birth" class="ts-w-full" format="DD-MM-YYYY"
            valueFormat="DD-MM-YYYY" :placeholder="$t('preInvoiceV2.dateOfBirth')" />
        </div>
        <div>
          <label class="compact-label">{{ $t('preInvoiceV2.lastNameKh') }}</label>
          <a-input v-model="model.student.last_name_kh" :placeholder="$t('preInvoiceV2.lastNameKh')" />
        </div>
        <div>
          <label class="compact-label">{{ $t('preInvoiceV2.firstNameKh') }}</label>
          <a-input v-model="model.student.first_name_kh" :placeholder="$t('preInvoiceV2.firstNameKh')" />
        </div>
        <div>
          <label class="required compact-label">{{ $t('preInvoiceV2.gender') }}</label>
          <a-select v-model="model.student.gender" class="ts-w-full" :placeholder="$t('preInvoiceV2.gender')">
            <a-select-option value="M">{{ $t('preInvoiceV2.male') }}</a-select-option>
            <a-select-option value="F">{{ $t('preInvoiceV2.female') }}</a-select-option>
          </a-select>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'StudentSummarySection',
  data() {
    return {
      studentKeyword: ''
    }
  },
  props: {
    model: { type: Object, required: true },
    selectedEnrollment: { type: Object, default: null },
    enrollments: { type: Array, required: true },
    loadingEnrollments: { type: Boolean, required: true },
    isEdit: { type: Boolean, default: false }
  },
  computed: {
    selectedStudentName() {
      return this.selectedEnrollment ? this.getEnrollmentName(this.selectedEnrollment) : ''
    },
    selectedStudentInitials() {
      return this.selectedEnrollment ? this.getEnrollmentInitials(this.selectedEnrollment) : ''
    },
    selectedStudentPhoto() {
      return this.selectedEnrollment ? this.enrollmentPhoto(this.selectedEnrollment) : ''
    }
  },
  watch: {
    'model.student_type'() {
      this.studentKeyword = ''
    }
  },
  methods: {
    searchEnrollments(value) {
      this.$emit('search-enrollments', value)
    },
    onStudentKeydown(event) {
      if (event.key === 'Enter') {
        event.preventDefault()
        this.searchEnrollments(this.studentKeyword)
      }
    },
    getEnrollmentName(record) {
      return `${record.last_name_en || ''} ${record.first_name_en || ''}`.trim()
    },
    getEnrollmentInitials(record) {
      const first = record.first_name_en ? record.first_name_en.charAt(0) : ''
      const last = record.last_name_en ? record.last_name_en.charAt(0) : ''
      return `${first}${last}`.toUpperCase() || '#'
    },
    enrollmentPhoto(record) {
      const photo = record ? record.photo || record.photo_url || record.student_photo : ''
      if (!photo) return ''
      if (/^(https?:)?\/\//.test(photo) || photo.indexOf('/api/files?filePath=') === 0) return photo
      const parts = String(photo).split(/file[_Pp]ath=/)
      return `/api/files?filePath=${parts[parts.length - 1]}`
    },
    teacherName(record) {
      return (record.teacher_name_foreign || record.teacher_name_native || '').trim() || '-'
    },
    academicStatusName(record) {
      if (Number(record.ac_status_id) === 1) return this.$t('preInvoiceV2.currentAcademic')
      if (Number(record.ac_status_id) === 3) return this.$t('preInvoiceV2.nextAcademic')
      return record.ac_status || '-'
    }
  }
}
</script>

<style scoped>
.compact-label {
  display: block;
  margin-bottom: 5px;
  font-size: 12px;
  font-weight: 600;
  color: #4b5563;
}

.new-student-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.student-select-field {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.student-select-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.student-select-label-row .compact-label {
  margin-bottom: 0;
}

.student-select-label-row span {
  color: #64748b;
  font-size: 11px;
  font-weight: 600;
}

.student-select-search-hint,
.student-select-helper {
  color: #6b7280;
  font-size: 11px;
  line-height: 1.35;
}

.student-select-search-hint {
  margin-top: -3px;
}

.student-select::v-deep .ant-select-selection {
  min-height: 38px;
  border-color: #93c5fd;
}

.student-select::v-deep .ant-select-selection__rendered {
  line-height: 36px;
}

.student-select::v-deep .ant-select-selection:focus,
.student-select::v-deep .ant-select-selection:active {
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.12);
}

.student-select-search-icon {
  color: #2563eb;
  cursor: pointer;
  font-size: 15px;
}

.student-option {
  min-width: 900px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0;
}

.student-option-heading {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.student-option-title {
  min-width: 0;
  display: flex;
  align-items: baseline;
  gap: 10px;
}

.student-option-avatar,
.student-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #dbeafe;
  color: #2563eb;
  font-weight: 700;
  flex: 0 0 auto;
  overflow: hidden;
}

.student-option-avatar img,
.student-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.student-option-avatar span,
.student-avatar span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.student-option-avatar {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  font-size: 11px;
}

.student-option-name {
  color: #111827;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.2;
}

.student-option-code {
  color: #4b5563;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
}

.student-option-grid {
  display: grid;
  grid-template-columns: 210px 210px 140px auto;
  gap: 6px 14px;
  align-items: start;
}

.student-option-field {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.student-option-field small {
  color: #9ca3af;
  font-size: 9px;
  font-weight: 700;
  line-height: 1.1;
  text-transform: uppercase;
}

.student-option-field strong {
  color: #374151;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.student-option-stack {
  min-width: 0;
  display: grid;
  gap: 5px;
}

.student-option-class {
  display: flex;
  align-items: center;
  gap: 6px;
}

.student-option-class span:first-child {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.student-option-status {
  width: max-content;
  max-width: 100%;
  flex: 0 0 auto;
  padding: 1px 6px;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8 !important;
}

.student-card {
  display: flex;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
}

.student-avatar {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  font-size: 15px;
}

.student-card-main {
  min-width: 0;
  flex: 1;
}

.student-card-name {
  color: #111827;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.student-card-meta {
  color: #6b7280;
  font-size: 12px;
  min-height: 18px;
}

.student-card-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 7px 12px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #e5e7eb;
}

.student-card-grid span {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  color: #4b5563;
  font-size: 11px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.student-card-grid i {
  color: #9ca3af;
  width: 13px;
  text-align: center;
}

@media (max-width: 1200px) {
  .new-student-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 992px) {

  .student-card-grid,
  .new-student-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .student-option {
    min-width: 680px;
  }

  .student-option-grid {
    grid-template-columns: 170px 170px 140px 140px;
  }

  .student-card-grid,
  .new-student-grid {
    grid-template-columns: 1fr;
  }
}
</style>
